import React, { useEffect, useState } from 'react';

import { Button, Input, InputNumber, notification, Form } from 'antd';
import { observer } from 'mobx-react';
import { Coatings, Delivery, Finishes, InputQuantities, Materials, Processes } from './DropDowns';
import QuoteStore from '../stores/QuoteStore';

import { ErrorRender, FormErrorParser } from '@partsbadger/library';

import PlasticMaterialPreferencesModal from '../components/QuoteLineItem/PlasticMaterialPreferencesModal';
import { isPlasticNeedingColor } from '@partsbadger/utils/lib/common/materialColor';
import { isStlFile, STL_MANUFACTURING_TYPE } from '@partsbadger/utils';

function hasErrors(fieldsError: any) {
    return Object.keys(fieldsError).some(field => fieldsError[field]);
}

interface Props {
    master_products: any;
    for_staff: boolean;
    handleClose: () => void;
}

interface IData {
    manufacturing_type?: string;
    product: {
        customs: any;
    };
}

const BulkUpdateItems = ({ master_products, for_staff = false, ...props }: Props) => {
    const [loading, setLoading] = useState(false);
    const [showQuantity, setShowQuantity] = useState(true);
    const [showMaterialColorModal, setShowMaterialColorModal] = useState<string | null>(null);

    const [form] = Form.useForm();

    const { getFieldValue } = form;

    const handleSubmit = async () => {
        form.validateFields()
            .then(async values => {
                setLoading(true);

                const data: IData = {
                    product: {
                        customs: {},
                    },
                };

                if (values.manufacturing_type) {
                    data['manufacturing_type'] = values.manufacturing_type;
                }

                if (values?.finish) {
                    data['product']['finishes'] = [values.finish.key];
                }

                if (values?.material) {
                    data['product']['material'] = values.material.key;
                }

                if (values.custom_material) {
                    data['product']['custom_material'] = values.custom_material;
                }

                if (values.custom_finish) {
                    data['product']['custom_finish'] = values.custom_finish;
                }

                if (values?.coating) {
                    data['product']['coatings'] = [values.coating.key];
                }

                if (values.custom_coating) {
                    data['product']['custom_coating'] = values.custom_coating;
                }

                if (values.quantity) {
                    data['quantity'] = values.quantity;
                    data['quantities'] = [];
                }

                if (values.quantities) {
                    data['quantity'] = 1;
                    data['quantities'] = values.quantities;
                }

                if (QuoteStore.quote) {
                    const payload = QuoteStore?.quote?.line_items
                        .filter(item => master_products.includes(item.product.master_product))
                        .map(item => {
                            const filename = item.product.attachments?.file3d?.filename;
                            if (
                                item.product.has_file_3d &&
                                filename &&
                                isStlFile(filename) &&
                                data.manufacturing_type &&
                                data.manufacturing_type !== STL_MANUFACTURING_TYPE
                            ) {
                                return {
                                    ...data,
                                    id: item.id,
                                    manufacturing_type: STL_MANUFACTURING_TYPE,
                                };
                            }

                            return {
                                ...data,
                                id: item.id,
                            };
                        });

                    await QuoteStore.updateMultipleLineItems(payload)
                        .then(() => {
                            form.resetFields();
                            props.handleClose();
                        })
                        .catch((err: any) => {
                            notification.error({
                                message: <ErrorRender error={err} />,
                                placement: 'topRight',
                            });
                        });
                }
                setLoading(false);
            })
            .catch(errorInfo => {
                notification.error({
                    key: 'form-errors',
                    duration: 8,
                    message: 'Please input all required fields',
                    description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                    placement: 'top',
                    maxCount: 1,
                });
            });
    };

    const handleOnSelectMaterial = e => {
        const materialLabel = e?.label || '';
        const materialId = e?.key;

        const containsClearInLabel = materialLabel.includes('Clear');

        if (containsClearInLabel) {
            const isPlasticMaterial = QuoteStore.materials?.some(
                material => material.id == materialId && material.material_type == 'Plastic'
            );

            if (isPlasticMaterial) {
                setShowMaterialColorModal('transparent');

                return true;
            }
        }

        const selectedMaterial = QuoteStore.materials?.find(material => material.id == materialId);
        const isColoredPlasticMaterial = isPlasticNeedingColor(selectedMaterial);

        if (isColoredPlasticMaterial) {
            setShowMaterialColorModal('colored');

            return true;
        }
    };

    const handleModalCancel = () => {
        setShowMaterialColorModal(null);
        form.resetFields(['material', 'isTransparent']);
    };

    const handleModalSubmit = () => {
        setShowMaterialColorModal(null);

        if (getFieldValue('isTransparent') == 'yes') {
            const finish = QuoteStore.finishes?.find(f => f.name === 'Flame Polish');

            form.setFieldsValue({ finish: { key: finish?.id, label: finish?.name } });
        }

        if (getFieldValue('preferredColor')) {
            const materialName = getFieldValue('material')?.label;
            const customMaterial = `${materialName} ${getFieldValue('preferredColor')}`;

            const material_values = QuoteStore.materials?.find(f => f.name === 'Custom');

            form.setFieldsValue({
                material: { key: material_values?.id, label: material_values?.name },
                custom_material: customMaterial,
            });
        }

        form.resetFields(['isTransparent', 'preferredColor']);
    };

    return (
        <Form
            form={form}
            className="form-bulk-update"
            layout={'vertical'}
            onFinish={() => handleSubmit()}
            onFinishFailed={errorInfo => {
                notification.error({
                    message: 'Error',
                    description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                });
            }}
        >
            <Form.Item label={'Process'}>
                <Form.Item
                    name="manufacturing_type"
                    rules={[{ required: false, message: 'Please select a process!' }]}
                    noStyle
                >
                    <Processes
                        placeholder="Select"
                        style={{
                            width: '100%',
                            minWidth: 100,
                        }}
                    />
                </Form.Item>
            </Form.Item>

            <Form.Item label={'Material'}>
                <Form.Item name="material" rules={[{ required: false, message: 'Please select a material!' }]} noStyle>
                    <Materials onSelect={handleOnSelectMaterial} materials={QuoteStore.materials} />
                </Form.Item>

                <Form.Item noStyle shouldUpdate={(prevValues, curValues) => prevValues.material !== curValues.material}>
                    {({ getFieldValue }) =>
                        getFieldValue('material')?.label === 'Custom' ? (
                            <Form.Item
                                name="custom_material"
                                label={'Material Name'}
                                rules={[{ required: true, message: 'Please insert a value' }]}
                            >
                                <Input size="small" />
                            </Form.Item>
                        ) : null
                    }
                </Form.Item>
            </Form.Item>
            <Form.Item label={'Finish'}>
                <Form.Item name="finish" rules={[{ required: false, message: 'Please select a finish!' }]} noStyle>
                    <Finishes finishes={QuoteStore.finishes} />
                </Form.Item>

                <Form.Item noStyle shouldUpdate={(prevValues, curValues) => prevValues.finish !== curValues.finish}>
                    {({ getFieldValue }) =>
                        getFieldValue('finish')?.label === 'Custom' ? (
                            <Form.Item
                                name="custom_finish"
                                label={'Finish Name'}
                                rules={[{ required: true, message: 'Please insert a value' }]}
                            >
                                <Input size="small" />
                            </Form.Item>
                        ) : null
                    }
                </Form.Item>
            </Form.Item>

            <Form.Item label={'Coating'}>
                <Form.Item name="coating" rules={[{ required: false, message: 'Please select a coating!' }]} noStyle>
                    <Coatings coatings={QuoteStore.coatings} />
                </Form.Item>

                <Form.Item noStyle shouldUpdate={(prevValues, curValues) => prevValues.coating !== curValues.coating}>
                    {({ getFieldValue }) =>
                        getFieldValue('coating')?.label === 'Custom' ? (
                            <Form.Item
                                name="custom_coating"
                                label={'Coating Name'}
                                rules={[{ required: true, message: 'Please insert a value' }]}
                            >
                                <Input size="small" />
                            </Form.Item>
                        ) : null
                    }
                </Form.Item>
            </Form.Item>

            {for_staff && (
                <>
                    {showQuantity ? (
                        <Form.Item labelCol={{ span: 24 }} label={'Quote Single Quantity'}>
                            <Form.Item name={'quantity'} noStyle>
                                <InputNumber step={1} controls={false} style={{ width: '100%' }} />
                            </Form.Item>
                            <Button type={'link'} onClick={() => setShowQuantity(false)}>
                                or Quote Multiple Quantities?
                            </Button>
                        </Form.Item>
                    ) : (
                        <Form.Item labelCol={{ span: 24 }} label={'Quote Multiple Quantities'}>
                            <Form.Item name={'quantities'} noStyle>
                                <InputQuantities style={{ width: '100%' }} />
                            </Form.Item>
                            <Button type={'link'} onClick={() => setShowQuantity(true)}>
                                or Quote Single Quantity?
                            </Button>
                        </Form.Item>
                    )}
                </>
            )}

            {!for_staff && !QuoteStore.isRequote && (
                <Form.Item
                    name="quantity"
                    label={'Quantity'}
                    rules={[{ required: false, message: 'Please input a quantity!' }]}
                >
                    <InputNumber />
                </Form.Item>
            )}

            <Form.Item>
                <Button type="primary" block={true} loading={loading} htmlType="submit">
                    Update All
                </Button>
            </Form.Item>

            {showMaterialColorModal && (
                <PlasticMaterialPreferencesModal
                    showMaterialColorModal={showMaterialColorModal}
                    handleSubmit={handleModalSubmit}
                    handleModalCancel={handleModalCancel}
                />
            )}
        </Form>
    );
};

export default observer(BulkUpdateItems);
